import React from 'react';

interface AuthFloatingInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  endAdornment?: React.ReactNode;
}

const AuthFloatingInput = React.forwardRef<HTMLInputElement, AuthFloatingInputProps>(
  ({ label, error, endAdornment, className = '', ...props }, ref) => {
    // Associate the error with the field programmatically, not just visually — a
    // screen reader otherwise never connects the two. Mirrors AuthFloatingSelect.
    const errorId = props.id ? `${props.id}-error` : undefined;
    const describedBy =
      [props['aria-describedby'], error && errorId ? errorId : undefined]
        .filter(Boolean)
        .join(' ') || undefined;

    return (
      <div className={className}>
        <div className="relative">
          <input
            {...props}
            ref={ref}
            placeholder=" "
            aria-invalid={error ? true : props['aria-invalid']}
            aria-describedby={describedBy}
            className={`peer w-full h-14 px-3.5 pt-4 pb-2 text-[15px] leading-6 bg-white dark:bg-grey-950 rounded-lg border outline-none transition-colors text-text-primary placeholder:text-transparent disabled:opacity-50 ${
              endAdornment ? 'pr-14' : ''
            } ${
              error
                ? 'border-error-500 focus:border-error-500'
                : 'border-border-primary/20 focus:border-primary-500'
            }`}
          />
          {/* Floating label — htmlFor keeps it announced by screen readers */}
          <label
            htmlFor={props.id}
            className={`pointer-events-none absolute left-3 px-0.5 text-xs font-semibold transition-[top,font-size,color] duration-150 bg-white dark:bg-grey-950 ${
              error
                ? 'text-error-500'
                : 'text-text-secondary peer-focus:text-primary-500'
            } -top-2 peer-placeholder-shown:top-4 peer-placeholder-shown:text-[15px] peer-placeholder-shown:font-normal peer-placeholder-shown:text-text-tertiary peer-focus:-top-2 peer-focus:text-xs peer-focus:font-semibold`}
          >
            {label}
          </label>
          {endAdornment && (
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center">
              {endAdornment}
            </div>
          )}
        </div>
        {/* role="alert" so a validation failure is announced, not just shown. */}
        {error && (
          <p id={errorId} role="alert" className="mt-1.5 text-xs text-error-500">
            {error}
          </p>
        )}
      </div>
    );
  }
);

AuthFloatingInput.displayName = 'AuthFloatingInput';

export default AuthFloatingInput;
